/**
 * TASK-VIS-004: Combat Log Service
 *
 * Manages combat log entries and notifies React components of updates.
 * Singleton pattern for global access.
 */

export type CombatLogEntryType = 'ability' | 'damage' | 'death' | 'movement' | 'buff' | 'system';

export interface CombatLogEntry {
  id: string;
  timestamp: number;
  tick: number;
  type: CombatLogEntryType;
  attackerName?: string;
  attackerId?: string;
  targetName?: string;
  targetId?: string;
  abilityName?: string;
  damage?: number;
  damageType?: string;
  message: string;
  icon: string;
}

type LogListener = (entries: CombatLogEntry[]) => void;

export class CombatLogService {
  private static instance: CombatLogService | null = null;

  private entries: CombatLogEntry[] = [];
  private listeners: Set<LogListener> = new Set();
  private maxEntries: number = 100;
  private currentTick: number = 0;

  private constructor() {}

  /**
   * Get singleton instance
   */
  public static getInstance(): CombatLogService {
    if (!CombatLogService.instance) {
      CombatLogService.instance = new CombatLogService();
    }
    return CombatLogService.instance;
  }

  /**
   * Reset instance (useful for testing or new combat)
   */
  public static resetInstance(): void {
    if (CombatLogService.instance) {
      CombatLogService.instance.clear();
    }
    CombatLogService.instance = null;
  }

  /**
   * Set current tick for new entries
   */
  public setCurrentTick(tick: number): void {
    this.currentTick = tick;
  }

  /**
   * Add ability use entry
   */
  public logAbility(attackerName: string, abilityName: string, targetName?: string): void {
    const formattedAbility = this.formatAbilityName(abilityName);
    const message = targetName
      ? `${attackerName} used ${formattedAbility} on ${targetName}`
      : `${attackerName} used ${formattedAbility}`;

    this.addEntry({
      type: 'ability',
      attackerName,
      abilityName: formattedAbility,
      targetName,
      message,
      icon: '⚔️'
    });
  }

  /**
   * Add damage entry
   */
  public logDamage(targetName: string, damage: number, damageType?: string): void {
    const typeStr = damageType ? ` ${damageType}` : '';
    const icon = this.getDamageIcon(damageType);

    this.addEntry({
      type: 'damage',
      targetName,
      damage,
      damageType,
      message: `${targetName} took ${damage}${typeStr} damage`,
      icon
    });
  }

  /**
   * Add death entry
   */
  public logDeath(unitName: string): void {
    this.addEntry({
      type: 'death',
      targetName: unitName,
      message: `${unitName} was defeated`,
      icon: '💀'
    });
  }

  /**
   * Add movement entry
   */
  public logMovement(unitName: string, movementType: string): void {
    const verb = this.getMovementVerb(movementType);
    this.addEntry({
      type: 'movement',
      attackerName: unitName,
      message: `${unitName} ${verb}`,
      icon: '⚡'
    });
  }

  /**
   * Add system message
   */
  public logSystem(message: string): void {
    this.addEntry({
      type: 'system',
      message,
      icon: '📢'
    });
  }

  /**
   * Core method to add entry
   */
  private addEntry(partial: Omit<CombatLogEntry, 'id' | 'timestamp' | 'tick'>): void {
    const entry: CombatLogEntry = {
      ...partial,
      id: `log_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      timestamp: Date.now(),
      tick: this.currentTick
    };

    this.entries.push(entry);

    // Trim old entries
    if (this.entries.length > this.maxEntries) {
      this.entries = this.entries.slice(-this.maxEntries);
    }

    // Notify listeners
    this.notifyListeners();
  }

  /**
   * Get all entries
   */
  public getEntries(): CombatLogEntry[] {
    return [...this.entries];
  }

  /**
   * Subscribe to log updates
   */
  public subscribe(callback: LogListener): () => void {
    this.listeners.add(callback);
    // Immediately call with current entries
    callback(this.getEntries());

    // Return unsubscribe function
    return () => {
      this.listeners.delete(callback);
    };
  }

  /**
   * Clear all entries
   */
  public clear(): void {
    this.entries = [];
    this.currentTick = 0;
    this.notifyListeners();
  }

  /**
   * Notify all listeners
   */
  private notifyListeners(): void {
    const entries = this.getEntries();
    this.listeners.forEach(callback => callback(entries));
  }

  /**
   * Format ability ID to display name
   */
  private formatAbilityName(abilityId: string): string {
    return abilityId
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }

  /**
   * Get icon for damage type
   */
  private getDamageIcon(damageType?: string): string {
    switch (damageType) {
      case 'ice': return '❄️';
      case 'fire': return '🔥';
      case 'lightning': return '⚡';
      case 'poison': return '☠️';
      case 'dark': return '🌑';
      case 'holy': return '✨';
      default: return '💥';
    }
  }

  /**
   * Get verb for movement type
   */
  private getMovementVerb(movementType: string): string {
    switch (movementType) {
      case 'teleport': return 'teleported';
      case 'charge': return 'charged';
      case 'flight': return 'flew';
      case 'burrow': return 'burrowed';
      default: return 'moved';
    }
  }
}

// Export singleton getter for convenience
export const getCombatLogService = () => CombatLogService.getInstance();
